import React, { createContext, useContext } from 'react';
import { useQuery } from "@tanstack/react-query";
import { useAuth } from '@/context/auth-provider';
import { useThemeManager } from '@/hooks/useThemeManager';
import { OrganizationService } from '@/services/organizationService';

interface OrganizationContextType {
  organization: any;
  membership: any;
  isLoading: boolean;
  error: any;
  subdomain: string;
  hasOrganizationAccess: boolean;
  organizationRole: string;
  isOrganizationAdmin: boolean;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export { OrganizationContext };

export const OrganizationProvider = ({ children }: { children: React.ReactNode }) => {
  const { user, loading: authLoading } = useAuth();
  const subdomain = OrganizationService.resolveOrganizationSubdomain();
  
  // Check if we're in a secure access context (user has secure_access metadata)
  const isSecureAccess = user?.user_metadata?.secure_access === true;
  
  console.log('[ORG-PROVIDER] Rendering with:', { 
    user: !!user, 
    userId: user?.id, 
    subdomain,
    authLoading,
    isSecureAccess
  });
  
  // Fetch organization
  const { data: organization, isLoading: orgLoading, error: orgError } = useQuery({
    queryKey: ['organization', subdomain],
    queryFn: () => OrganizationService.fetchOrganization(subdomain),
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });

  console.log('[ORG-PROVIDER] Organization query result:', { 
    organization: !!organization, 
    orgId: organization?.id,
    orgName: organization?.name,
    orgLoading, 
    orgError: !!orgError 
  });

  // Check user's membership (skip for secure access tokens as they have limited scope)
  const { data: membership, isLoading: membershipLoading, error: membershipError } = useQuery({
    queryKey: ['organization-membership', user?.id, organization?.id],
    queryFn: () => OrganizationService.fetchMembership(user!.id, organization!.id),
    enabled: !!user && !!organization && !authLoading && !isSecureAccess, // Skip membership for secure access
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  });

  console.log('[ORG-PROVIDER] Membership query result:', { 
    membership: !!membership, 
    membershipRole: membership?.role,
    membershipLoading, 
    membershipError: !!membershipError,
    queryEnabled: !!user && !!organization && !authLoading && !isSecureAccess,
    isSecureAccess
  });

  // Compute derived state
  const isLoading = orgLoading || membershipLoading || authLoading;
  const error = orgError || membershipError;
  
  // For secure access, assume basic access without membership checks
  const hasOrganizationAccess = isSecureAccess ? true : (!!user && !!membership);
  const organizationRole = isSecureAccess 
    ? (user?.user_metadata?.token_type === 'org_admin' ? 'admin' : 'member')
    : (membership?.role || null);
  const isOrganizationAdmin = organizationRole === 'admin';
  
  console.log('[ORG-PROVIDER] Final computed values:', {
    isLoading,
    hasOrganizationAccess,
    organizationRole,
    isOrganizationAdmin,
    error: !!error,
    authLoading,
    isSecureAccess
  });
  
  // Apply theme configuration if organization has custom branding
  useThemeManager();

  return (
    <OrganizationContext.Provider value={{ 
      organization, 
      membership,
      isLoading, 
      error, 
      subdomain,
      hasOrganizationAccess,
      organizationRole,
      isOrganizationAdmin
    }}>
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganizationContext = () => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganizationContext must be used within an OrganizationProvider');
  }
  return context;
};